/* eslint-disable no-underscore-dangle */
import { Box, CircularProgress, Grid, SvgIcon } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import DateFnsAdapter from '@date-io/date-fns';
import {
  AppFormikSelect,
  AppFormikTextField,
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
} from 'shared/components/AppComponents';
import { useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import { ReactComponent as Search } from 'assets/images/ic_search.svg';
import DatasetsInventoryCard from './DatasetsInventoryCard';
import { useDispatch, useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';
import { PaginationActions } from 'state/pagination/Action';

const dateFns = new DateFnsAdapter();

interface dataSetsInventory {
  _id: string;
  datasetName: string;
  owningTeam: string;
  contactPerson: string;
  purposeOfUse: string;
  lifecycleStage: string;
  sourceType: string;
  dataSensitivityClassification: string;
  approvalStatus: string;
  riskTier: string;
  watermarkingTechnique: string;
  sourceDetails: string;
  dataCollectionMethod: string;
  geographicOrigin: string;
  volumeSize: string;
  rententionPolicy: string;
  accessControls: string;
  encryption: string;
  sharingRestrictions: string;
  dataDictionaryLink: string;
  metadataDescription: string;
  knownDataQualityIssues: string;
  validationSummary: string;
  metadataAvailable: boolean;
  datasheetAvailable: boolean;
  dataModalities: string;
  sensitiveDataTypesPresent: string;
  complianceTags: string;
  downstreamDependencies: string;
  reviewFrequency: string;
  monitoringInPlace: boolean;
  organizationId: null | string;
  createdBy: null | string;
  updatedBy: null | string;
  updatedData: null | string;
  isActive: boolean;
  deletedAt: null | string;
  createdDate: string;
  __v: number;
}

interface datasetsInventoryProps {
  inventoryData: dataSetsInventory[];
  onSearch: (search: string) => void;
  loading: boolean;
}

const DatasetsInventoryList: React.FC<datasetsInventoryProps> = (
  props
): React.ReactElement => {
  const dispatch = useDispatch();
  const datasetPagination = useSelector(
    (state: IAppState) => state.paginationState.dataset
  );

  const navigation = useNavigate();
  const { inventoryData, onSearch, loading } = props;

  const handleStatusChange = (statusValue: string): void => {
    dispatch(
      PaginationActions.setPagination({
        dataset: {
          ...datasetPagination,
          offset: 1,
          status: statusValue,
        },
      })
    );
  };

  const handleClearFilter = (): void => {
    dispatch(
      PaginationActions.setPagination({
        dataset: {
          ...datasetPagination,
          offset: 1,
          search: '',
          status: '',
        },
      })
    );
  };

  return (
    <Box className="datasets-list-wrapper">
      <Formik
        initialValues={{
          'search-datasets': datasetPagination.search || '',
          status: datasetPagination.status || '',
        }}
        enableReinitialize
        onSubmit={() => console.log('search-datasets')}
      >
        {({ resetForm }) => (
          <>
            {/* <SearchFormikContext onChange={handleFilterChange} /> */}

            <Grid
              container
              className="datasets-list-nav"
              spacing={2}
              alignItems="flex-start"
            >
              <Grid item xs={12} sm={6} md={6} lg={7}>
                <Box className="dataset-search">
                  <AppFormikTextField
                    inputProps={{
                      startAdornment: (
                        <SvgIcon
                          component={Search}
                          inheritViewBox
                          className="view-icon"
                          fontSize="inherit"
                          htmlColor="#76A81F"
                        />
                      ),
                    }}
                    outerLabel={false}
                    placeholder="Search by name.."
                    name="search-datasets"
                    onChange={(e: any) => {
                      onSearch(e.target.value);
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={3} md={3}>
                <Box width="100%">
                  <AppFormikSelect
                    name="status"
                    className="rework-selection mt-2"
                    notched={false}
                    labelId="fix-selection-label"
                    testid="solicitor-dropdown"
                    MenuProps={{
                      className: 'menu-fix-selection single-fix-rework',
                    }}
                    placeholder="Filter Status"
                    notEmphasized
                    notSelectablePlaceholder={false}
                    items={[
                      'All Status',
                      'Approved',
                      'Pending',
                      'Draft',
                      'Archived',
                      'Decommissioned',
                    ]}
                    onChange={(e: any) => {
                      handleStatusChange(e.target.value);
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={3} md={3} lg={2}>
                <AppPrimaryButtonOutlined
                  fullWidth
                  title="Clear Filters"
                  onClick={() => {
                    resetForm();
                    handleClearFilter();
                  }}
                />
              </Grid>
            </Grid>
          </>
        )}
      </Formik>
      <Box className="table-datasets-list">
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell align="left">Name</TableCell>
                <TableCell align="left">Risk level</TableCell>
                <TableCell align="left">Status</TableCell>
                <TableCell align="left">Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading &&
                inventoryData.length > 0 &&
                inventoryData.map((row: any) => (
                  <TableRow
                    key={row._id}
                    sx={{
                      '& .MuiTableCell-root': { whiteSpace: 'nowrap' },
                      '&:last-child td, &:last-child th': {
                        border: 0,
                      },
                    }}
                  >
                    <TableCell component="th" scope="row">
                      {row._id}
                    </TableCell>
                    <TableCell align="left" className="word-break">
                      {row.datasetName}
                    </TableCell>
                    <TableCell align="left">{row.riskTier}</TableCell>
                    <TableCell align="left">{row.approvalStatus}</TableCell>
                    <TableCell align="left">
                      {dateFns.date(row.createdAt).toDateString()}
                    </TableCell>
                    <TableCell align="right">
                      <AppPrimaryButton
                        title="View"
                        isLoading={loading}
                        disabled={false}
                        className="primary-light-fill btn-extra-small"
                        onClick={() => {
                          navigation(`/datasets/records/${row._id}`);
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}

              {!loading && inventoryData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <DatasetsInventoryCard loading={loading} />
                  </TableCell>
                </TableRow>
              )}

              {loading && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ border: 0 }}>
                    <Box className="empty-content">
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default DatasetsInventoryList;
